import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { catchError, finalize, map, Observable, of } from 'rxjs';

import { AppService } from '../../services/app.service';
import { ClientService } from '../../services/client/client.service';
import { Logger } from '../../services/logger/logger.service';

const LOGGER = new Logger('ClientContextGuard');

/**
 * Résout le dossier porté par le segment `/c/:clientId` (#173).
 *
 * ⚠️ C'est un **garde**, et surtout pas un resolver : Angular exécute tous les
 * gardes (racine → feuille) *avant* les resolvers, or `CountryGuard` et
 * `FeatureGuard` lisent `appService.selectedClient` de façon synchrone sur les
 * routes enfants. Posé ici, le chargement est terminé avant qu'ils ne s'exécutent.
 */
@Injectable({
    providedIn: 'root'
})
export class ClientContextGuard {
    private _appService = inject(AppService);
    private _clientService = inject(ClientService);
    private _router = inject(Router);

    canActivate(route: ActivatedRouteSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
        const rawClientId = route.paramMap.get('clientId');
        const clientId = Number(rawClientId);

        if (!Number.isInteger(clientId) || clientId <= 0) {
            LOGGER.warn('Identifiant de dossier invalide dans l\'URL', rawClientId);
            return this._router.createUrlTree(['/home']);
        }

        // Dossier déjà chargé : on ne recharge pas à chaque navigation interne
        if (this._appService.selectedClient?.id === clientId) {
            return true;
        }

        LOGGER.debug('Chargement du dossier porté par l\'URL', clientId);
        this._appService.uiBlocked = true;

        // `get$` sélectionne l'instance en cas de succès — `AppService` est à l'écoute
        // de `selected$` et alimente `selectedClient`.
        //
        // ⚠️ En cas d'échec (404, 403, dossier hors droits), le flux **se termine en
        // erreur** : `ServiceBase.handleError` lève après avoir posté le message, ce
        // qui rend inatteignable le `of(false)` de son `catchError`. Sans le
        // `catchError` ci-dessous, la navigation échouerait sans redirection et
        // laisserait l'utilisateur sur place.
        return this._clientService.get$(clientId).pipe(
            map((client: any) => {
                if (!client) {
                    LOGGER.warn('Dossier introuvable, redirection vers la sélection', clientId);
                    return this._router.createUrlTree(['/home']);
                }
                return true;
            }),
            catchError(() => {
                LOGGER.warn('Dossier inaccessible, redirection vers la sélection', clientId);
                return of(this._router.createUrlTree(['/home']));
            }),
            finalize(() => {
                this._appService.uiBlocked = false;
            })
        );
    }
}
